/**
 * Rhythm Trainer v1 (Phase 3 Tier 3) — pure-function core.
 *
 * Two sub-modes: Meter ID (5 meters — 4/4, 3/4, 6/8, 5/4, 7/8) and Subdivision
 * ID (4 subdivisions — straight 8ths, 16ths, triplets, swing 8ths). The pool
 * tables are Bowie-locked; grouping + accent positions encode the canonical
 * pedagogical contrasts (notably 6/8 vs 3/4 — see Bowie scope memo §6/8 trap).
 *
 * Mirrors the Ear Training analyzer file-for-file: pool tables, two question
 * generators with injectable RNG, two validators, two session-stat folds. No
 * music-context dependency — the audition primitive (click-engine) consumes
 * the MeterSpec / SubdivisionSpec directly.
 *
 * Spec: docs/superpowers/specs/2026-05-22-rhythm-trainer-v1-design.md §4
 * Bowie scope memo: .claude/agent-memory/bowie/project_rhythm_trainer_v1_scope.md
 */

export type MeterKind = '4-4' | '3-4' | '6-8' | '5-4' | '7-8'

export interface MeterSpec {
  topNumber: number
  bottomNumber: number
  /** Beat groupings, sum = topNumber. Compound (6/8) groups into 3+3. */
  grouping: readonly number[]
  /**
   * Zero-indexed beat positions that receive a primary or secondary accent.
   * Position 0 is the downbeat (always accented). For 6/8: [0, 3] — the
   * accent on position 3 is what discriminates 6/8 from 3/4 to the ear.
   */
  accentPositions: readonly number[]
}

export interface MeterEntry {
  kind: MeterKind
  lexiconId: string
  displayName: string
  displayNameShort: string
  spec: MeterSpec
}

/**
 * Bowie-locked meter table. Do not reorder — the 5-button welcome grid renders
 * in this order.
 */
export const METER_POOL: readonly MeterEntry[] = [
  {
    kind: '4-4',
    lexiconId: 'rhythm-meter-4-4',
    displayName: '4/4',
    displayNameShort: '4/4',
    spec: { topNumber: 4, bottomNumber: 4, grouping: [4], accentPositions: [0] },
  },
  {
    kind: '3-4',
    lexiconId: 'rhythm-meter-3-4',
    displayName: '3/4',
    displayNameShort: '3/4',
    spec: { topNumber: 3, bottomNumber: 4, grouping: [3], accentPositions: [0] },
  },
  {
    kind: '6-8',
    lexiconId: 'rhythm-meter-6-8',
    displayName: '6/8',
    displayNameShort: '6/8',
    spec: { topNumber: 6, bottomNumber: 8, grouping: [3, 3], accentPositions: [0, 3] },
  },
  {
    kind: '5-4',
    lexiconId: 'rhythm-meter-5-4',
    displayName: '5/4',
    displayNameShort: '5/4',
    spec: { topNumber: 5, bottomNumber: 4, grouping: [3, 2], accentPositions: [0, 3] },
  },
  {
    kind: '7-8',
    lexiconId: 'rhythm-meter-7-8',
    displayName: '7/8',
    displayNameShort: '7/8',
    spec: { topNumber: 7, bottomNumber: 8, grouping: [2, 2, 3], accentPositions: [0, 2, 4] },
  },
]

export type SubdivisionKind = '8ths' | '16ths' | 'triplets' | 'swing'

export interface SubdivisionSpec {
  kind: SubdivisionKind
  notesPerBeat: number
  /** 'even' for 8ths/16ths/triplets; 'long-short' for swing. */
  articulation: 'even' | 'long-short'
}

export interface SubdivisionEntry {
  kind: SubdivisionKind
  lexiconId: string
  displayName: string
  displayNameShort: string
  spec: SubdivisionSpec
}

/**
 * Bowie-locked subdivision table. Do not reorder — the 4-button welcome grid
 * renders in this order.
 */
export const SUBDIVISION_POOL: readonly SubdivisionEntry[] = [
  {
    kind: '8ths',
    lexiconId: 'rhythm-sub-8ths',
    displayName: 'Straight 8ths',
    displayNameShort: '8ths',
    spec: { kind: '8ths', notesPerBeat: 2, articulation: 'even' },
  },
  {
    kind: '16ths',
    lexiconId: 'rhythm-sub-16ths',
    displayName: 'Straight 16ths',
    displayNameShort: '16ths',
    spec: { kind: '16ths', notesPerBeat: 4, articulation: 'even' },
  },
  {
    kind: 'triplets',
    lexiconId: 'rhythm-sub-triplets',
    displayName: 'Triplets',
    displayNameShort: 'Triplets',
    spec: { kind: 'triplets', notesPerBeat: 3, articulation: 'even' },
  },
  {
    kind: 'swing',
    lexiconId: 'rhythm-sub-swing',
    displayName: 'Swing 8ths',
    displayNameShort: 'Swing',
    spec: { kind: 'swing', notesPerBeat: 2, articulation: 'long-short' },
  },
]

export interface MeterQuestion {
  entry: MeterEntry
}

export interface GenerateMeterQuestionOptions {
  focusedMeter?: MeterKind
}

/**
 * Generate a fresh random meter question.
 *
 * RNG draw order:
 *  1. `rng()` → meter index into `METER_POOL`.
 *
 * When `focusedMeter` is provided and matches a pool entry, returns that entry
 * deterministically (used by the `?meter=` URL-param focused-meter mode).
 */
export const generateMeterQuestion = (
  rng: () => number = Math.random,
  options: GenerateMeterQuestionOptions = {}
): MeterQuestion => {
  if (options.focusedMeter) {
    const entry = METER_POOL.find(e => e.kind === options.focusedMeter)
    if (entry) {
      return { entry }
    }
  }
  return { entry: METER_POOL[Math.floor(rng() * METER_POOL.length)] }
}

export interface SubdivisionQuestion {
  entry: SubdivisionEntry
}

/**
 * Generate a fresh random subdivision question.
 *
 * RNG draw order:
 *  1. `rng()` → subdivision index into `SUBDIVISION_POOL`.
 */
export const generateSubdivisionQuestion = (
  rng: () => number = Math.random
): SubdivisionQuestion => ({
  entry: SUBDIVISION_POOL[Math.floor(rng() * SUBDIVISION_POOL.length)],
})

export interface MeterVerdict {
  correct: boolean
  picked: MeterKind
  entry: MeterEntry
}

export interface SubdivisionVerdict {
  correct: boolean
  picked: SubdivisionKind
  entry: SubdivisionEntry
}

export const validateMeterAnswer = (q: MeterQuestion, picked: MeterKind): MeterVerdict => ({
  correct: picked === q.entry.kind,
  picked,
  entry: q.entry,
})

export const validateSubdivisionAnswer = (
  q: SubdivisionQuestion,
  picked: SubdivisionKind
): SubdivisionVerdict => ({
  correct: picked === q.entry.kind,
  picked,
  entry: q.entry,
})

export interface MeterSessionResult {
  question: MeterQuestion
  verdict: MeterVerdict
}

export interface SubdivisionSessionResult {
  question: SubdivisionQuestion
  verdict: SubdivisionVerdict
}

export interface RhythmSessionStats {
  total: number
  correct: number
}

export const computeMeterSessionStats = (
  results: MeterSessionResult[]
): RhythmSessionStats => ({
  total: results.length,
  correct: results.filter(r => r.verdict.correct).length,
})

export const computeSubdivisionSessionStats = (
  results: SubdivisionSessionResult[]
): RhythmSessionStats => ({
  total: results.length,
  correct: results.filter(r => r.verdict.correct).length,
})

// ─── Grouping ID (v1.5) ───

export type GroupingMeterKind = '5-4' | '7-8'

export interface GroupingEntry {
  meter: GroupingMeterKind
  /** Stable id — answer key + lexicon key. e.g. '7-8-2-2-3'. */
  groupingId: string
  /** Learner-facing label, e.g. '2+2+3'. */
  label: string
  lexiconId: string
  /** Full MeterSpec for the click-engine audition (grouping + accentPositions). */
  spec: MeterSpec
}

/**
 * Bowie-locked grouping table. accentPositions = prefix-sums of `grouping`
 * (each group's start index is accented). Do not reorder — answer grids render
 * in this order within a meter.
 */
export const GROUPING_POOL: readonly GroupingEntry[] = [
  {
    meter: '5-4',
    groupingId: '5-4-3-2',
    label: '3+2',
    lexiconId: 'rhythm-grouping-5-4-3-2',
    spec: { topNumber: 5, bottomNumber: 4, grouping: [3, 2], accentPositions: [0, 3] },
  },
  {
    meter: '5-4',
    groupingId: '5-4-2-3',
    label: '2+3',
    lexiconId: 'rhythm-grouping-5-4-2-3',
    spec: { topNumber: 5, bottomNumber: 4, grouping: [2, 3], accentPositions: [0, 2] },
  },
  {
    meter: '7-8',
    groupingId: '7-8-2-2-3',
    label: '2+2+3',
    lexiconId: 'rhythm-grouping-7-8-2-2-3',
    spec: { topNumber: 7, bottomNumber: 8, grouping: [2, 2, 3], accentPositions: [0, 2, 4] },
  },
  {
    meter: '7-8',
    groupingId: '7-8-3-2-2',
    label: '3+2+2',
    lexiconId: 'rhythm-grouping-7-8-3-2-2',
    spec: { topNumber: 7, bottomNumber: 8, grouping: [3, 2, 2], accentPositions: [0, 3, 5] },
  },
  {
    meter: '7-8',
    groupingId: '7-8-2-3-2',
    label: '2+3+2',
    lexiconId: 'rhythm-grouping-7-8-2-3-2',
    spec: { topNumber: 7, bottomNumber: 8, grouping: [2, 3, 2], accentPositions: [0, 2, 5] },
  },
]

export interface GroupingQuestion {
  entry: GroupingEntry
  options: readonly GroupingEntry[]
}

const GROUPING_METERS: readonly GroupingMeterKind[] = ['5-4', '7-8']

/**
 * Generate a fresh random grouping question.
 *
 * RNG draw order:
 *  1. `rng()` → meter index into GROUPING_METERS (5/4 or 7/8, 50/50).
 *  2. `rng()` → grouping index within that meter's options.
 *
 * Drawing the meter first gives each meter equal airtime regardless of how
 * many groupings it has.
 */
export const generateGroupingQuestion = (rng: () => number = Math.random): GroupingQuestion => {
  const meter = GROUPING_METERS[Math.floor(rng() * GROUPING_METERS.length)]
  const options = GROUPING_POOL.filter(g => g.meter === meter)
  const entry = options[Math.floor(rng() * options.length)]
  return { entry, options }
}

export interface GroupingVerdict {
  correct: boolean
  pickedGroupingId: string
  entry: GroupingEntry
}

export const validateGroupingAnswer = (
  q: GroupingQuestion,
  pickedGroupingId: string
): GroupingVerdict => ({
  correct: pickedGroupingId === q.entry.groupingId,
  pickedGroupingId,
  entry: q.entry,
})

export interface GroupingSessionResult {
  question: GroupingQuestion
  verdict: GroupingVerdict
}

export const computeGroupingSessionStats = (
  results: GroupingSessionResult[]
): RhythmSessionStats => ({
  total: results.length,
  correct: results.filter(r => r.verdict.correct).length,
})
